// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useVariant } from '@calendly/feature-check-react';
import { usePageTitle } from '@calendly/hooks';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { ArrowExpandIcon, QuestionCircleIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { PageHeader } from '@calendly/ui/components/page-header';
import { BodyText } from '@calendly/ui/components/typography';
import { textColorL1, textColorL2 } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { Container } from 'components/organizations/layout';
import { LearnMoreButton } from 'components/page_elements';
import { useContextualHelp } from 'features/contextual_help';
import { CONTENT_PAGES, useNewEmptyStates } from 'features/empty_state';
import { ExportButton } from 'features/event_type_export';
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context';
import { isAdminOrOwnerSelector } from 'selectors/current_user';
import { Content } from './content';
import { CreateDropdownButton } from './create_dropdown_button';
import { isManagedEventTypesFeatureEnabledSelector } from './selectors';
const headerContainerCss = "hmntfm6";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, props.className)} />;
});
const actionsContainerCss = "atryi2h";
const ActionsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionsContainerCss, props.className)} />;
});
const articleLink = 'https://calendly.com/help/managed-events-overview';
const HelpArticleLink = ({
  children
}) => <Link href={articleLink} target="_blank" rel="noopener noreferrer" underline={false}>
    {children}
    <ArrowExpandIcon inline />
  </Link>;
const styledExportButtonCss = "s1peoovo";
const StyledExportButton = forwardRef((props, ref) => <ExportButton ref={ref} {...props} className={classNames(styledExportButtonCss, props.className)} />);
export const Page = () => {
  const inSettingsModal = useIsSettingsModalLayout();
  const isNewEmptyStateEnabled = useNewEmptyStates();
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const isManagedEventTypesFeatureEnabled = useSelector(isManagedEventTypesFeatureEnabledSelector);
  const metPaywallDelayExperiment = useVariant('met_paywall_delay_experiment');
  const shouldDelayPaywall = metPaywallDelayExperiment === 'on';
  const disabled = !isManagedEventTypesFeatureEnabled && !shouldDelayPaywall;
  const {
    open: openContextualHelp
  } = useContextualHelp();
  const description = HtmlParser(I18n.t('publisher.managed_event_types.list.description'), {
    HelpArticleLink
  });
  const onClickLearnMore = () => {
    openContextualHelp({
      contentKey: CONTENT_PAGES.managedEventTypes
    });
  };
  usePageTitle(I18n.t('publisher.admin.managed_events.title'));
  if (isNewEmptyStateEnabled) {
    return <Container>
        {!inSettingsModal && <HeaderContainer>
            <PageHeader eyebrow={<BodyText weight="bold" color={textColorL2}>
                  {I18n.t('publisher.admin.navigation.templates')}
                </BodyText>} title={I18n.t('publisher.admin.navigation.managed_events')} protip={<LearnMoreButton onClick={onClickLearnMore} aria-label={`${CONTENT_PAGES.managedEventTypes} ${I18n.t('publisher.empty_state.learn_more')}`}>
                  <QuestionCircleIcon />
                </LearnMoreButton>} actions={[<CreateDropdownButton />]} />
          </HeaderContainer>}
        <Content />
      </Container>;
  }
  return <Container>
      {!inSettingsModal && <PageHeader eyebrow={<BodyText weight="bold" color={textColorL2}>
              {I18n.t('publisher.admin.navigation.templates')}
            </BodyText>} title={I18n.t('publisher.admin.navigation.managed_events')} actions={[<ActionsContainer>
              {isAdminOrOwner && <StyledExportButton decoration="ghost" disabled={disabled} />}
              <CreateDropdownButton disabled={disabled} />
            </ActionsContainer>]} description={<BodyText color={textColorL1}>
              {description}
            </BodyText>} />}
      <Content />
    </Container>;
};

require("./page.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./page.js");